import { supabaseClient } from './supabase';
import { supabaseLocationService } from './supabaseLocationService';

type RawRow = Record<string, unknown>;

const asNumber = (value: unknown): number | null => {
  if (value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const getFirstNumber = (row: RawRow, keys: string[]): number | null => {
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(row, key)) {
      const n = asNumber(row[key]);
      if (n != null) return n;
    }
  }
  return null;
};

const getFirstString = (row: RawRow, keys: string[]): string | null => {
  for (const key of keys) {
    const value = row[key];
    if (value != null && String(value).trim() !== '') return String(value);
  }
  return null;
};

const normalizeYear = (row: RawRow): number | null => {
  return getFirstNumber(row, ['year', 'analysis_year', 'vintage_year']);
};

export interface SolarPotentialPoint {
  year: number;
  technicalPotentialMw: number | null;
  economicPotentialMw: number | null;
  generationMwh: number | null;
  locationName: string | null;
}

export interface SolarPotentialResult {
  rows: SolarPotentialPoint[];
  chartData: {
    labels: number[];
    datasets: Array<{
      label: string;
      data: (number | null)[];
      borderColor: string;
      backgroundColor: string;
      borderWidth: number;
      tension: number;
      pointRadius: number;
      spanGaps: boolean;
      yAxisID?: string;
    }>;
  } | null;
  chartOptions: Record<string, unknown> | null;
  latestYear: number | null;
  latestTechnicalPotentialMw: number | null;
  latestEconomicPotentialMw: number | null;
  latestGenerationMwh: number | null;
}

type SolarPotentialChartDataset = NonNullable<SolarPotentialResult['chartData']>['datasets'][number];

export async function fetchSolarTechnicalPotential(
  geofips: number | string | null | undefined,
  locationName?: string
): Promise<SolarPotentialResult> {
  const gRaw = geofips != null ? String(geofips).trim() : '';
  const gNormalized = gRaw ? (gRaw.replace(/^0+/, '') || '0') : '';
  // #region agent log
  fetch('http://127.0.0.1:7601/ingest/0966bb5c-6daa-4d7e-ba25-e84a55b66c1f',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'bdfc61'},body:JSON.stringify({sessionId:'bdfc61',runId:'pre-fix',hypothesisId:'H1',location:'solarTechnicalPotentialService.ts:entry',message:'fetchSolarTechnicalPotential called',data:{geofipsRaw:gRaw,geofipsNormalized:gNormalized,locationName:locationName??null},timestamp:Date.now()})}).catch(()=>{});
  // #endregion

  if (!gNormalized) {
    // #region agent log
    fetch('http://127.0.0.1:7601/ingest/0966bb5c-6daa-4d7e-ba25-e84a55b66c1f',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'bdfc61'},body:JSON.stringify({sessionId:'bdfc61',runId:'post-fix',hypothesisId:'H8',location:'solarTechnicalPotentialService.ts:noGeofips',message:'Skipping solar potential query because geofips is unavailable',data:{geofipsRaw:gRaw,locationName:locationName??null},timestamp:Date.now()})}).catch(()=>{});
    // #endregion
    return {
      rows: [],
      chartData: null,
      chartOptions: null,
      latestYear: null,
      latestTechnicalPotentialMw: null,
      latestEconomicPotentialMw: null,
      latestGenerationMwh: null,
    };
  }

  const padded7 = gNormalized.padStart(7, '0').slice(-7);
  const geoCandidates = Array.from(new Set([gNormalized, padded7]));
  // #region agent log
  fetch('http://127.0.0.1:7601/ingest/0966bb5c-6daa-4d7e-ba25-e84a55b66c1f',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'bdfc61'},body:JSON.stringify({sessionId:'bdfc61',runId:'post-fix',hypothesisId:'H9',location:'solarTechnicalPotentialService.ts:geoCandidates',message:'Trying geofips candidate formats',data:{geoCandidates},timestamp:Date.now()})}).catch(()=>{});
  // #endregion

  // #region agent log
  fetch('http://127.0.0.1:7601/ingest/0966bb5c-6daa-4d7e-ba25-e84a55b66c1f',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'bdfc61'},body:JSON.stringify({sessionId:'bdfc61',runId:'pre-fix',hypothesisId:'H2',location:'solarTechnicalPotentialService.ts:queryByGeofips',message:'Querying solar potential by geofips candidates',data:{geofips:gNormalized,padded7},timestamp:Date.now()})}).catch(()=>{});
  // #endregion
  let { data, error } = await supabaseClient
    .from('solar_technical_potential')
    .select('*')
    .in('geofips', geoCandidates);

  if (!error && (!data || data.length === 0)) {
    const geofipsNumber = Number(gNormalized);
    if (Number.isFinite(geofipsNumber)) {
      // #region agent log
      fetch('http://127.0.0.1:7601/ingest/0966bb5c-6daa-4d7e-ba25-e84a55b66c1f',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'bdfc61'},body:JSON.stringify({sessionId:'bdfc61',runId:'post-fix',hypothesisId:'H10',location:'solarTechnicalPotentialService.ts:queryByNumericGeofips',message:'Retrying solar potential query with numeric geofips',data:{geofipsNumber},timestamp:Date.now()})}).catch(()=>{});
      // #endregion
      const retry = await supabaseClient
        .from('solar_technical_potential')
        .select('*')
        .eq('geofips', geofipsNumber);
      if (!retry.error && retry.data && retry.data.length > 0) {
        data = retry.data;
        error = retry.error;
      }
    }
  }

  if (!error && (!data || data.length === 0)) {
    const probe = await supabaseClient
      .from('solar_technical_potential')
      .select('*')
      .limit(10);
    // #region agent log
    fetch('http://127.0.0.1:7601/ingest/0966bb5c-6daa-4d7e-ba25-e84a55b66c1f',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'bdfc61'},body:JSON.stringify({sessionId:'bdfc61',runId:'post-fix',hypothesisId:'H13',location:'solarTechnicalPotentialService.ts:probeRowCount',message:'Diagnostic probe completed',data:{requestedGeofips:gNormalized,probeRowCount:(probe.data||[]).length,probeError:probe.error?.message??null},timestamp:Date.now()})}).catch(()=>{});
    // #endregion
    if (!probe.error && probe.data && probe.data.length > 0) {
      const sampleRows = probe.data as RawRow[];
      const sampleGeofips = sampleRows.map((r) => r.geofips ?? null).slice(0, 10);
      const sampleYears = sampleRows.map((r) => r.year ?? null).slice(0, 10);
      const sampleSources = sampleRows.map((r) => r.source ?? null).slice(0, 10);
      // #region agent log
      fetch('http://127.0.0.1:7601/ingest/0966bb5c-6daa-4d7e-ba25-e84a55b66c1f',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'bdfc61'},body:JSON.stringify({sessionId:'bdfc61',runId:'post-fix',hypothesisId:'H11',location:'solarTechnicalPotentialService.ts:noMatchProbe',message:'No geofips match; sampled table rows for diagnostics',data:{requestedGeofips:gNormalized,requestedPadded7:padded7,sampleGeofips,sampleYears,sampleSources,sampleColumns:Object.keys(sampleRows[0] || {})},timestamp:Date.now()})}).catch(()=>{});
      // #endregion
    } else if (probe.error) {
      // #region agent log
      fetch('http://127.0.0.1:7601/ingest/0966bb5c-6daa-4d7e-ba25-e84a55b66c1f',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'bdfc61'},body:JSON.stringify({sessionId:'bdfc61',runId:'post-fix',hypothesisId:'H12',location:'solarTechnicalPotentialService.ts:probeError',message:'Diagnostic probe query failed',data:{message:probe.error.message,details:probe.error.details,hint:probe.error.hint,code:probe.error.code},timestamp:Date.now()})}).catch(()=>{});
      // #endregion
    }
  }

  if (error) {
    // #region agent log
    fetch('http://127.0.0.1:7601/ingest/0966bb5c-6daa-4d7e-ba25-e84a55b66c1f',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'bdfc61'},body:JSON.stringify({sessionId:'bdfc61',runId:'pre-fix',hypothesisId:'H4',location:'solarTechnicalPotentialService.ts:queryError',message:'Supabase query failed',data:{message:error.message,details:error.details,hint:error.hint,code:error.code},timestamp:Date.now()})}).catch(()=>{});
    // #endregion
    throw new Error(error.message || 'Failed to fetch solar technical potential');
  }
  // #region agent log
  fetch('http://127.0.0.1:7601/ingest/0966bb5c-6daa-4d7e-ba25-e84a55b66c1f',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'bdfc61'},body:JSON.stringify({sessionId:'bdfc61',runId:'pre-fix',hypothesisId:'H5',location:'solarTechnicalPotentialService.ts:querySuccess',message:'Supabase query succeeded',data:{rowCount:(data||[]).length},timestamp:Date.now()})}).catch(()=>{});
  // #endregion

  const normalizedRows: SolarPotentialPoint[] = (data || [])
    .map((row: RawRow) => {
      const year = normalizeYear(row);
      if (year == null) return null;
      return {
        year,
        technicalPotentialMw: getFirstNumber(row, ['technical_potential_mw', 'value']),
        economicPotentialMw: getFirstNumber(row, ['economic_potential_mw']),
        generationMwh: getFirstNumber(row, ['potential_generation_mwh']),
        locationName: getFirstString(row, ['location_name', 'city', 'county']),
      };
    })
    .filter((row): row is SolarPotentialPoint => row != null)
    .sort((a, b) => a.year - b.year);

  if (normalizedRows.length === 0 && locationName) {
    const countyName = supabaseLocationService.getCountyForCity(locationName);
    if (countyName) {
      const countyGeofips = await supabaseLocationService.getGeofips(countyName);
      // #region agent log
      fetch('http://127.0.0.1:7601/ingest/0966bb5c-6daa-4d7e-ba25-e84a55b66c1f',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'bdfc61'},body:JSON.stringify({sessionId:'bdfc61',runId:'post-fix',hypothesisId:'H14',location:'solarTechnicalPotentialService.ts:countyFallback',message:'Trying county-level fallback for solar potential',data:{locationName,countyName,countyGeofips},timestamp:Date.now()})}).catch(()=>{});
      // #endregion
      if (countyGeofips != null) {
        const countyRaw = String(countyGeofips).trim();
        const countyNorm = countyRaw.replace(/^0+/, '') || '0';
        const countyPad5 = countyNorm.padStart(5, '0').slice(-5);
        const countyCandidates = Array.from(new Set([countyNorm, countyPad5]));
        const countyQuery = await supabaseClient
          .from('solar_technical_potential')
          .select('*')
          .in('geofips', countyCandidates);
        if (!countyQuery.error && countyQuery.data && countyQuery.data.length > 0) {
          // #region agent log
          fetch('http://127.0.0.1:7601/ingest/0966bb5c-6daa-4d7e-ba25-e84a55b66c1f',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'bdfc61'},body:JSON.stringify({sessionId:'bdfc61',runId:'post-fix',hypothesisId:'H15',location:'solarTechnicalPotentialService.ts:countyFallbackSuccess',message:'County-level fallback returned rows',data:{countyCandidates,rowCount:countyQuery.data.length},timestamp:Date.now()})}).catch(()=>{});
          // #endregion
          const countyRows: SolarPotentialPoint[] = countyQuery.data
            .map((row: RawRow) => {
              const year = normalizeYear(row);
              if (year == null) return null;
              return {
                year,
                technicalPotentialMw: getFirstNumber(row, ['technical_potential_mw', 'value']),
                economicPotentialMw: getFirstNumber(row, ['economic_potential_mw']),
                generationMwh: getFirstNumber(row, ['potential_generation_mwh']),
                locationName: getFirstString(row, ['location_name', 'city', 'county']),
              };
            })
            .filter((row): row is SolarPotentialPoint => row != null)
            .sort((a, b) => a.year - b.year);
          if (countyRows.length > 0) {
            normalizedRows.push(...countyRows);
          }
        }
      }
    }
  }

  const dedupedRows = Array.from(
    new Map(normalizedRows.map((r) => [r.year, r])).values()
  ).sort((a, b) => a.year - b.year);

  const labels = dedupedRows.map((r) => r.year);
  const technicalData = dedupedRows.map((r) => r.technicalPotentialMw);
  const economicData = dedupedRows.map((r) => r.economicPotentialMw);
  const generationData = dedupedRows.map((r) => r.generationMwh);

  const hasTechnical = technicalData.some((v) => v != null);
  const hasEconomic = economicData.some((v) => v != null);
  const hasGeneration = generationData.some((v) => v != null);

  const datasets: SolarPotentialChartDataset[] = [];
  if (hasTechnical) {
    datasets.push({
      label: 'Technical potential (MW)',
      data: technicalData,
      borderColor: '#1d4ed8',
      backgroundColor: 'rgba(29, 78, 216, 0.15)',
      borderWidth: 2,
      tension: 0.2,
      pointRadius: 2,
      spanGaps: true,
      yAxisID: 'y',
    });
  }
  if (hasEconomic) {
    datasets.push({
      label: 'Economic potential (MW)',
      data: economicData,
      borderColor: '#0ea5e9',
      backgroundColor: 'rgba(14, 165, 233, 0.15)',
      borderWidth: 2,
      tension: 0.2,
      pointRadius: 2,
      spanGaps: true,
      yAxisID: 'y',
    });
  }
  if (hasGeneration) {
    datasets.push({
      label: 'Potential generation (MWh)',
      data: generationData,
      borderColor: '#16a34a',
      backgroundColor: 'rgba(22, 163, 74, 0.15)',
      borderWidth: 2,
      tension: 0.2,
      pointRadius: 2,
      spanGaps: true,
      yAxisID: hasTechnical || hasEconomic ? 'y1' : 'y',
    });
  }

  const chartData = labels.length && datasets.length
    ? { labels, datasets }
    : null;

  const chartOptions = chartData
    ? {
        type: 'line',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: true, position: 'top' },
          title: { display: false },
        },
        scales: {
          y: {
            beginAtZero: true,
            title: { display: true, text: 'Potential (MW)' },
          },
          ...(hasGeneration && (hasTechnical || hasEconomic)
            ? {
                y1: {
                  position: 'right',
                  beginAtZero: true,
                  grid: { drawOnChartArea: false },
                  title: { display: true, text: 'Potential generation (MWh)' },
                },
              }
            : {}),
          x: {
            title: { display: true, text: 'Year' },
          },
        },
      }
    : null;

  const latest = dedupedRows.length ? dedupedRows[dedupedRows.length - 1] : null;

  return {
    rows: dedupedRows,
    chartData,
    chartOptions,
    latestYear: latest?.year ?? null,
    latestTechnicalPotentialMw: latest?.technicalPotentialMw ?? null,
    latestEconomicPotentialMw: latest?.economicPotentialMw ?? null,
    latestGenerationMwh: latest?.generationMwh ?? null,
  };
}

